import {
  S3Client,
  PutObjectCommand,
  GetObjectCommand,
  DeleteObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
} from '@aws-sdk/client-s3';
import { createHash } from 'crypto';
import chalk from 'chalk';
import ora from 'ora';
import type {
  CloudCacheProvider,
  CacheItem,
  CacheMeta,
  CacheStats,
  CacheConfig,
} from './types';

export class CloudCache implements CloudCacheProvider {
  private client: S3Client;
  private bucket: string;
  private prefix: string;
  private spinner = ora();

  constructor(config: CacheConfig['cloud']) {
    this.bucket = config.bucket;
    this.prefix = 'synthia-cache/';

    this.client = new S3Client({
      region: config.region,
      credentials: {
        accessKeyId: config.accessKeyId,
        secretAccessKey: config.accessKeySecret,
      },
      endpoint: config.endpoint,
    });
  }

  /**
   * 获取缓存
   */
  async get(key: string): Promise<CacheItem | null> {
    this.spinner.start(`从云端获取缓存: ${key}`);

    try {
      const objectKey = this.getObjectKey(key);
      const command = new GetObjectCommand({
        Bucket: this.bucket,
        Key: objectKey,
      });

      const response = await this.client.send(command);

      if (!response.Body) {
        this.spinner.warn(`云端缓存不存在: ${key}`);
        return null;
      }

      // 读取响应体
      const chunks: Uint8Array[] = [];
      const reader = response.Body.transformToWebStream().getReader();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }

      const buffer = Buffer.concat(chunks);
      const data = JSON.parse(buffer.toString());

      this.spinner.succeed(`云端缓存获取成功: ${key}`);
      return data;
    } catch (error) {
      this.spinner.fail(`云端缓存获取失败: ${key}`);
      console.error(chalk.red('云端缓存错误:'), error);
      return null;
    }
  }

  /**
   * 设置缓存
   */
  async set(key: string, value: any, meta: Partial<CacheMeta>): Promise<void> {
    this.spinner.start(`设置云端缓存: ${key}`);

    try {
      const cacheItem: CacheItem = {
        key,
        value,
        meta: {
          hash: meta.hash || this.calculateHash(value),
          size: meta.size || JSON.stringify(value).length,
          expiresAt: meta.expiresAt || Date.now() + 7 * 24 * 60 * 60 * 1000, // 7天
          dependencies: meta.dependencies || [],
          tags: meta.tags || [],
          version: meta.version || '1.0.0',
        },
        createdAt: Date.now(),
        lastAccessedAt: Date.now(),
        accessCount: 0,
      };

      const objectKey = this.getObjectKey(key);
      const command = new PutObjectCommand({
        Bucket: this.bucket,
        Key: objectKey,
        Body: JSON.stringify(cacheItem),
        ContentType: 'application/json',
        Metadata: {
          'synthia-cache-key': key,
          'synthia-cache-version': cacheItem.meta.version,
          'synthia-cache-hash': cacheItem.meta.hash,
        },
      });

      await this.client.send(command);
      this.spinner.succeed(`云端缓存设置完成: ${key}`);
    } catch (error) {
      this.spinner.fail(`云端缓存设置失败: ${key}`);
      console.error(chalk.red('云端缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 删除缓存
   */
  async delete(key: string): Promise<void> {
    this.spinner.start(`删除云端缓存: ${key}`);

    try {
      const objectKey = this.getObjectKey(key);
      const command = new DeleteObjectCommand({
        Bucket: this.bucket,
        Key: objectKey,
      });

      await this.client.send(command);
      this.spinner.succeed(`云端缓存删除完成: ${key}`);
    } catch (error) {
      this.spinner.fail(`云端缓存删除失败: ${key}`);
      console.error(chalk.red('云端缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 清空缓存
   */
  async clear(): Promise<void> {
    this.spinner.start('清空云端缓存...');

    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucket,
        Prefix: this.prefix,
      });

      const response = await this.client.send(command);

      if (response.Contents) {
        const deletePromises = response.Contents.map(obj =>
          this.client.send(
            new DeleteObjectCommand({
              Bucket: this.bucket,
              Key: obj.Key!,
            })
          )
        );

        await Promise.all(deletePromises);
      }

      this.spinner.succeed('云端缓存已清空');
    } catch (error) {
      this.spinner.fail('清空云端缓存失败');
      console.error(chalk.red('云端缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 检查缓存是否存在
   */
  async has(key: string): Promise<boolean> {
    try {
      const objectKey = this.getObjectKey(key);
      const command = new HeadObjectCommand({
        Bucket: this.bucket,
        Key: objectKey,
      });

      await this.client.send(command);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 获取缓存统计信息
   */
  async getStats(): Promise<CacheStats> {
    this.spinner.start('获取云端缓存统计...');

    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucket,
        Prefix: this.prefix,
      });

      const response = await this.client.send(command);

      let totalItems = 0;
      let totalSize = 0;
      const topItems: Array<{
        key: string;
        accessCount: number;
        size: number;
      }> = [];

      if (response.Contents) {
        totalItems = response.Contents.length;
        totalSize = response.Contents.reduce(
          (sum, obj) => sum + (obj.Size || 0),
          0
        );

        // 获取前10个最大的缓存项
        const sortedItems = response.Contents.sort(
          (a, b) => (b.Size || 0) - (a.Size || 0)
        ).slice(0, 10);

        for (const item of sortedItems) {
          if (item.Key) {
            const key = this.extractKeyFromObjectKey(item.Key);
            topItems.push({
              key,
              accessCount: 0, // 云端无法获取访问次数
              size: item.Size || 0,
            });
          }
        }
      }

      const stats: CacheStats = {
        totalItems,
        totalSize,
        hits: 0, // 云端无法统计命中率
        misses: 0,
        hitRate: 0,
        avgAccessTime: 0,
        topItems,
      };

      this.spinner.succeed('云端缓存统计获取完成');
      return stats;
    } catch (error) {
      this.spinner.fail('获取云端缓存统计失败');
      console.error(chalk.red('云端缓存错误:'), error);
      throw error;
    }
  }

  /**
   * 获取所有缓存键
   */
  async keys(): Promise<string[]> {
    try {
      const command = new ListObjectsV2Command({
        Bucket: this.bucket,
        Prefix: this.prefix,
      });

      const response = await this.client.send(command);

      if (!response.Contents) {
        return [];
      }

      return response.Contents.map(obj => obj.Key)
        .filter((key): key is string => key !== undefined)
        .map(key => this.extractKeyFromObjectKey(key));
    } catch (error) {
      console.error(chalk.red('获取缓存键失败:'), error);
      return [];
    }
  }

  /**
   * 获取缓存大小
   */
  async size(): Promise<number> {
    const stats = await this.getStats();
    return stats.totalSize;
  }

  /**
   * 同步到云端
   */
  async sync(key: string): Promise<void> {
    this.spinner.start(`同步缓存到云端: ${key}`);

    try {
      // 这里需要从本地缓存获取数据
      // 实际实现中会调用本地缓存提供者
      this.spinner.succeed(`缓存同步完成: ${key}`);
    } catch (error) {
      this.spinner.fail(`缓存同步失败: ${key}`);
      console.error(chalk.red('同步错误:'), error);
      throw error;
    }
  }

  /**
   * 从云端拉取
   */
  async pull(key: string): Promise<CacheItem | null> {
    return this.get(key);
  }

  /**
   * 批量同步
   */
  async batchSync(keys: string[]): Promise<void> {
    this.spinner.start(`批量同步 ${keys.length} 个缓存到云端...`);

    try {
      const syncPromises = keys.map(key => this.sync(key));
      await Promise.all(syncPromises);

      this.spinner.succeed(`批量同步完成: ${keys.length} 个缓存`);
    } catch (error) {
      this.spinner.fail('批量同步失败');
      console.error(chalk.red('批量同步错误:'), error);
      throw error;
    }
  }

  /**
   * 获取对象键
   */
  private getObjectKey(key: string): string {
    return `${this.prefix}${key}.json`;
  }

  /**
   * 从对象键提取缓存键
   */
  private extractKeyFromObjectKey(objectKey: string): string {
    return objectKey.replace(this.prefix, '').replace('.json', '');
  }

  /**
   * 计算哈希值
   */
  private calculateHash(value: any): string {
    const hash = createHash('sha256');
    hash.update(JSON.stringify(value));
    return hash.digest('hex');
  }
}
